import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, type Location } from "react-router";
import { Button, Icon, Input, useToast } from "@astrum/ui";
import { ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { brandInitials, useBranding } from "../lib/branding.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./login.css";

type LocationState = { from?: Location } | null;

/**
 * Splits a workspace name into a plain first word and an accented remainder,
 * reproducing the "ASTRUM|CONNECT" treatment for whatever it is called now.
 * A single-word name simply gets no accent — better than colouring half a word
 * at an arbitrary point.
 */
function splitWordmark(name: string): { head: string; tail: string } {
  const trimmed = name.trim();
  const gap = trimmed.indexOf(" ");
  if (gap === -1) return { head: trimmed, tail: "" };
  return { head: trimmed.slice(0, gap), tail: trimmed.slice(gap + 1) };
}

/**
 * Two-step form: email/password, then (when the account has 2FA enabled) a
 * six-digit code. Both steps share one error box and one submit button so the
 * layout doesn't jump between steps.
 */
export function Login() {
  usePageTitle("Log in");
  const { signIn, verifyTwoFactor, sessionEnded } = useAuth();
  const { workspaceName, workspaceLogo } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const wordmark = splitWordmark(workspaceName);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Back to exactly where they were sent away from — query string included.
   *
   * `.pathname` alone silently dropped it, so signing in from a link to
   * a deep link landed on the screen it named
   * tab instead of the Amazon one. Every piece of state this app puts in the
   * URL — the open channel tab, catalogue search and filters — was lost the
   * same way, which reads as the link being wrong rather than the redirect.
   */
  // Errors raised by the session that just ended ("Authentication is
  // required", ×n) describe nothing any more. Cleared on arrival and again on
  // signing in, since requests already in flight can still fail after this
  // screen appears.
  const { clear: clearToasts } = useToast();
  useEffect(() => {
    if (sessionEnded) clearToasts();
  }, [sessionEnded, clearToasts]);

  const from = (location.state as LocationState)?.from;
  const redirectTo = from ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}` : "/home";

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (challengeId) {
        await verifyTwoFactor(challengeId, code);
        clearToasts();
        navigate(redirectTo, { replace: true });
        return;
      }

      const result = await signIn(email, password);
      if (result.requiresTwoFactor && result.challengeId) {
        setChallengeId(result.challengeId);
        return;
      }
      clearToasts();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <div className="login-glow" aria-hidden="true" />

      <div className="login-card">
        <div className="login-brand">
          <span className="login-mark">
            {workspaceLogo ? (
              <img src={workspaceLogo} alt="" className="login-mark-img" />
            ) : (
              brandInitials(workspaceName)
            )}
          </span>
          <span className="login-wordmark">
            {wordmark.head}
            {wordmark.tail && <span className="login-wordmark-accent">{wordmark.tail}</span>}
          </span>
        </div>

        <h1 className="login-heading">Welcome back.</h1>
        <p className="login-subcopy">
          {challengeId
            ? "Enter the six-digit code from your authenticator app."
            : sessionEnded
              ? "Your session ended. Sign in again to pick up where you left off."
              : "Sign in with your company account."}
        </p>

        {error && (
          <div className="login-error" role="alert">
            <Icon name="warning" size={16} />
            <span>{error}</span>
          </div>
        )}

        <form className="login-form" onSubmit={(e) => void handleSubmit(e)}>
          {challengeId ? (
            <Input
              label="Verification code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              autoFocus
              required
            />
          ) : (
            <>
              <Input
                label="Email"
                type="email"
                autoComplete="username"
                placeholder="you@astrum.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Link to="/forgot-password" className="login-forgot">
                Forgot password?
              </Link>
            </>
          )}

          <Button type="submit" fullWidth loading={submitting} disabled={challengeId ? code.length !== 6 : false}>
            {challengeId ? "Verify" : "Sign in"}
          </Button>
        </form>

        <div className="login-footer">
          <span className="login-footer-dot" aria-hidden="true" />
          <span>
            <strong>Private company system.</strong> Your access and security events are recorded.
          </span>
        </div>
      </div>
    </main>
  );
}
